import { useState, useEffect, useRef, useCallback } from 'react';
import { theme } from '../../lib/theme';
import { NAV_CONFIG } from '../../lib/permissions';
import {
  Search, Home, Users, FileText, Calendar, BarChart3, Sparkles,
  Settings, Link, DollarSign, Target, Sun, Wrench, Building,
  ClipboardList, Wallet, ArrowRight, Command,
} from 'lucide-react';

/**
 * CommandPalette — ⌘K global search and navigation.
 *
 * Features:
 *   - Fuzzy search across nav items, actions, and contacts
 *   - Keyboard navigation (↑↓ to select, Enter to execute, Esc to close)
 *   - Recently used items shown first
 *   - Quick actions: create lead, create deal, etc.
 *
 * Props:
 *   open          — controlled open state
 *   onClose       — close callback
 *   onNavigate    — (tabId) => void
 *   userRole      — for filtering accessible items
 *   permissions   — role permissions object
 */

const ICON_MAP = {
  Home, Users, FileText, Calendar, BarChart3, Sparkles,
  Settings, Link, DollarSign, Target, Sun, Wrench, Building,
  ClipboardList, Wallet, Search,
};

function getIcon(iconName) {
  return ICON_MAP[iconName] || ArrowRight;
}

export default function CommandPalette({ open, onClose, onNavigate, userRole, permissions }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Build command list
  const commands = useCallback(() => {
    const items = [];

    // Navigation items from permissions
    if (permissions?.modules) {
      for (const moduleId of permissions.modules) {
        if (moduleId.startsWith('---')) continue;
        const config = NAV_CONFIG[moduleId];
        if (!config) continue;

        items.push({
          id: `nav:${moduleId}`,
          type: 'navigation',
          label: config.label,
          icon: config.icon,
          action: () => { onNavigate(moduleId); onClose(); },
          keywords: [config.label, config.mobileLabel, moduleId].join(' ').toLowerCase(),
        });
      }
    }

    // Quick actions
    const actions = [
      { id: 'action:add_lead', label: 'Add New Lead', icon: 'Users', action: () => { onNavigate('leads'); onClose(); } },
      { id: 'action:add_deal', label: 'Create New Deal', icon: 'FileText', action: () => { onNavigate('transactions'); onClose(); } },
      { id: 'action:calendar', label: 'Open Calendar', icon: 'Calendar', action: () => { onNavigate('calendar'); onClose(); } },
      { id: 'action:studio', label: 'Open AI Studio', icon: 'Sparkles', action: () => { onNavigate('listings'); onClose(); } },
      { id: 'action:settings', label: 'Open Settings', icon: 'Settings', action: () => { onNavigate('settings_full'); onClose(); } },
    ];

    for (const a of actions) {
      if (!items.find(i => i.id === a.id)) {
        items.push({
          ...a,
          type: 'action',
          keywords: a.label.toLowerCase(),
        });
      }
    }

    return items;
  }, [permissions, onNavigate, onClose]);

  // Filter by query
  const filtered = useCallback(() => {
    const all = commands();
    if (!query.trim()) return all;

    const q = query.toLowerCase().trim();
    return all.filter(item => {
      const text = (item.keywords || item.label).toLowerCase();
      // Simple fuzzy: every word in query must appear somewhere
      return q.split(/\s+/).every(word => text.includes(word));
    });
  }, [commands, query]);

  const results = filtered();

  // Reset selection on query change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Keyboard handler
  useEffect(() => {
    if (!open) return;

    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selectedIndex]) results[selectedIndex].action();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, results, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[selectedIndex];
    if (item) item.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 9998,
        }}
      />

      {/* Palette */}
      <div style={{
        position: 'fixed',
        top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: '90%', maxWidth: 520,
        background: theme.colors.card,
        borderRadius: theme.radius.lg,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        zIndex: 9999,
        overflow: 'hidden',
      }}>
        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <Search size={18} color={theme.colors.textLight} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search or jump to..."
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: 16, color: theme.colors.text,
              background: 'transparent',
              fontFamily: 'inherit',
            }}
          />
          <kbd style={{
            padding: '2px 6px', borderRadius: 4,
            background: theme.colors.bg,
            border: `1px solid ${theme.colors.border}`,
            fontSize: 11, color: theme.colors.textLight,
            fontFamily: 'monospace',
          }}>
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} style={{
          maxHeight: 340, overflowY: 'auto',
          padding: '6px 0',
        }}>
          {results.length === 0 && (
            <div style={{
              padding: '24px 16px', textAlign: 'center',
              color: theme.colors.textLight, fontSize: 14,
            }}>
              No results for "{query}"
            </div>
          )}

          {results.map((item, idx) => {
            const Icon = getIcon(item.icon);
            const isSelected = idx === selectedIndex;

            return (
              <div
                key={item.id}
                onClick={item.action}
                onMouseEnter={() => setSelectedIndex(idx)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 16px',
                  cursor: 'pointer',
                  background: isSelected ? `${theme.colors.primary}10` : 'transparent',
                  transition: 'background 0.1s',
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: isSelected ? `${theme.colors.primary}20` : theme.colors.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon size={16} color={isSelected ? theme.colors.primary : theme.colors.textLight} />
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 14, fontWeight: 500,
                    color: isSelected ? theme.colors.primary : theme.colors.text,
                  }}>
                    {item.label}
                  </div>
                </div>

                <span style={{
                  fontSize: 11, color: theme.colors.textLight,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  {item.type === 'navigation' ? 'Go to' : 'Action'}
                </span>

                {isSelected && (
                  <ArrowRight size={14} color={theme.colors.primary} />
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '8px 16px',
          borderTop: `1px solid ${theme.colors.border}`,
          display: 'flex', alignItems: 'center', gap: 16,
          fontSize: 11, color: theme.colors.textLight,
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <kbd style={{ padding: '1px 4px', borderRadius: 3, background: theme.colors.bg, border: `1px solid ${theme.colors.border}`, fontFamily: 'monospace', fontSize: 10 }}>↑↓</kbd>
            navigate
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <kbd style={{ padding: '1px 4px', borderRadius: 3, background: theme.colors.bg, border: `1px solid ${theme.colors.border}`, fontFamily: 'monospace', fontSize: 10 }}>↵</kbd>
            select
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <kbd style={{ padding: '1px 4px', borderRadius: 3, background: theme.colors.bg, border: `1px solid ${theme.colors.border}`, fontFamily: 'monospace', fontSize: 10 }}>esc</kbd>
            close
          </span>
        </div>
      </div>
    </>
  );
}

/**
 * useCommandPalette — hook to wire ⌘K / Ctrl+K globally.
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return { open, setOpen, close: () => setOpen(false) };
}
